import { useState, useEffect, useRef } from "react";
import { publicApi, BACKEND_URL } from "@/lib/api";
import axios from "axios";
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  ExternalLink,
  Shield,
  Loader2,
  CheckSquare,
  Square,
  Share2,
  Bookmark,
  AlertCircle,
  CheckCircle2,
  Info,
} from "lucide-react";

const COUNCIL_URL =
  "https://yourvoicebucks.citizenspace.com/planning/2aa4b313/consultation/";

const CONSENT_TEXT =
  "I confirm that I am submitting this response on my own behalf, from my own device. " +
  "I understand my IP address will be recorded by Aylesbury Hindu Temple CIO for GDPR and audit purposes, " +
  "and that Aylesbury Hindu Temple (www.aylesburyhindutemple.org) stores my session data to track submission progress. " +
  "I can request deletion of my data at any time by contacting us via www.aylesburyhindutemple.org.";

const SHARE_MESSAGE =
  "I just submitted my response to the Buckinghamshire Council Local Plan Sites Survey to support a Hindu temple within or nearby Aylesbury. " +
  "You can do the same — it only takes about 15 minutes: ";

const APP_URL = window.location.origin;

// Validation helpers
function validateStreetName(value) {
  if (!value) return null;
  const t = value.trim();
  if (t.length < 2) return "Must be at least 2 characters";
  if (t.length > 100) return "Must be under 100 characters";
  if (/[<>"{}|\\^`]/.test(t)) return "Contains invalid characters";
  return null;
}

function validateYears(value) {
  if (!value) return null;
  const t = value.trim();
  if (!/^\d+$/.test(t)) return "Must be a whole number";
  const n = parseInt(t, 10);
  if (n < 0 || n > 100) return "Must be between 0 and 100";
  return null;
}

function isMobile() {
  return window.innerWidth < 768;
}

// ─────────────────────────────────────────────────────────────
// Mobile blocker
// ─────────────────────────────────────────────────────────────
function MobileBlocker() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm text-center">
        <div className="text-5xl mb-4">💻</div>
        <h1 className="text-xl font-bold text-gray-900 mb-3">
          Please use a laptop or desktop
        </h1>
        <p className="text-gray-600 text-sm leading-relaxed mb-4">
          This tool requires a desktop browser to drag the bookmark to your
          bookmarks bar. Mobile support is coming soon.
        </p>
        <p className="text-xs text-gray-400">
          Aylesbury Hindu Temple CIO — charity no. 1209109
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Animated drag hint — shows a cursor dragging a bookmark up
// into a mock bookmarks bar. Pure CSS, works on all browsers.
// ─────────────────────────────────────────────────────────────
function DragHint() {
  return (
    <div className="relative w-full max-w-xs mx-auto select-none" style={{ height: 120 }}>
      {/* Mock bookmarks bar */}
      <div className="absolute top-0 left-0 right-0 h-8 bg-gray-100 border border-gray-300 rounded-lg flex items-center px-3 gap-2">
        <span className="text-[10px] text-gray-400 font-medium">Bookmarks bar</span>
        <span className="bg-white border border-gray-200 rounded px-2 py-0.5 text-[9px] text-gray-500">Other bookmark</span>
        {/* Drop target highlight — flashes when "bookmark" arrives */}
        <span className="drag-hint-target bg-indigo-100 border border-dashed border-indigo-400 rounded px-2 py-0.5 text-[9px] text-indigo-600 opacity-0">
          AHTemple Batch 1
        </span>
      </div>

      {/* Animated bookmark chip + cursor */}
      <div className="drag-hint-chip absolute flex items-center gap-1 bg-indigo-600 text-white text-[10px] font-semibold px-2.5 py-1 rounded-lg shadow-lg whitespace-nowrap">
        <Bookmark size={10} />
        AHTemple Batch 1/7
      </div>

      {/* Cursor */}
      <svg className="drag-hint-cursor absolute w-5 h-5" viewBox="0 0 24 24" fill="none">
        <path d="M5 3l14 10-6.5 1.5L16 21l-3 1.5-3.5-7L4 18V3z" fill="#333" stroke="#fff" strokeWidth="1"/>
      </svg>

      <style>{`
        .drag-hint-chip {
          left: 50%;
          transform: translateX(-50%);
          animation: drag-up 2.5s ease-in-out infinite;
        }
        .drag-hint-cursor {
          animation: cursor-drag 2.5s ease-in-out infinite;
        }
        .drag-hint-target {
          animation: target-flash 2.5s ease-in-out infinite;
        }
        @keyframes drag-up {
          0%, 15% { top: 75px; opacity: 1; }
          55%, 70% { top: 4px; opacity: 1; left: 50%; transform: translateX(-50%) scale(0.85); }
          75% { opacity: 0; top: 4px; }
          100% { opacity: 0; top: 75px; }
        }
        @keyframes cursor-drag {
          0%, 15% { left: calc(50% + 40px); top: 88px; opacity: 1; }
          55%, 70% { left: calc(50% + 30px); top: 18px; opacity: 1; }
          75% { opacity: 0; }
          100% { opacity: 0; }
        }
        @keyframes target-flash {
          0%, 60% { opacity: 0; }
          65%, 80% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Batch workflow — council allows max 5 sites per session
// ─────────────────────────────────────────────────────────────
function BatchWorkflow({ batches, totalSites, batchSize }) {
  const [copiedIdx, setCopiedIdx] = useState(-1);

  const handleCopy = async (href, idx) => {
    try {
      await navigator.clipboard.writeText(href);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(-1), 2000);
    } catch {
      /* clipboard not available */
    }
  };

  const totalBatches = batches.length;

  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 space-y-6">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="text-green-600 shrink-0" size={24} />
        <h3 className="font-bold text-green-900 text-lg">
          Your bookmarks are ready!
        </h3>
      </div>

      {/* Batch explanation */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
        The council form only allows <strong>{batchSize} sites at a time</strong>.
        Your {totalSites} sites have been split into{" "}
        <strong>{totalBatches} bookmarks</strong>. Follow the steps below to
        run them one by one.
      </div>

      {/* Step 1 — drag ALL bookmarks */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Step 1 — Save all {totalBatches} bookmarks
        </p>
        <p className="text-sm text-gray-500">
          Drag each blue button below up to your browser's bookmarks bar
          (the thin bar just below the address bar).
          {" "}If you can't see it, press{" "}
          <kbd className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">
            Ctrl+Shift+B
          </kbd>{" "}
          (Chrome/Edge) or{" "}
          <kbd className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">
            Cmd+Shift+B
          </kbd>{" "}
          (Mac) to show it.
        </p>
        <DragHint />
        <div className="space-y-2">
          {batches.map((batch, idx) => {
            const label = `AHTemple Batch ${idx + 1}/${totalBatches}`;
            return (
              <div key={idx} className="flex items-center gap-3">
                <a
                  ref={(node) => {
                    if (node) node.setAttribute("href", batch.loader_bookmarklet);
                  }}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2.5 rounded-xl cursor-grab active:cursor-grabbing shadow-md transition-all hover:shadow-lg select-none text-sm"
                  onClick={(e) => e.preventDefault()}
                  draggable
                  title={`Drag "${label}" to your bookmarks bar`}
                >
                  <Bookmark size={14} />
                  {label}
                </a>
                <button
                  onClick={() => handleCopy(batch.loader_bookmarklet, idx)}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg px-2 py-1.5 transition-colors"
                  title="Copy bookmark code"
                >
                  {copiedIdx === idx ? (
                    <Check size={12} className="text-green-600" />
                  ) : (
                    <Copy size={12} />
                  )}
                  {copiedIdx === idx ? "Copied!" : "Copy"}
                </button>
                <span className="text-xs text-gray-400">
                  {batch.site_count} sites
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-green-200" />

      {/* IMPORTANT WARNING */}
      <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <span className="text-red-500 text-xl">⚠️</span>
          <div>
            <p className="font-bold text-red-700">IMPORTANT: Keep this page open!</p>
            <p className="text-sm text-red-600 mt-1">
              Open the council form in a <strong>NEW TAB</strong> (right-click → "Open in new tab"). 
              Do NOT navigate away from this page. You need to come back here to confirm your submission.
            </p>
          </div>
        </div>
      </div>

      {/* Step 2 — workflow */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Step 2 — Run each bookmark, one at a time
        </p>

        <div className="space-y-4 text-sm text-gray-700">
          {/* Step 2a */}
          <div className="flex gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs shrink-0 mt-0.5">1</span>
            <div>
              <p className="font-medium">Open the council form in a NEW TAB</p>
              <p className="text-gray-500 text-xs mt-0.5">
                <strong className="text-red-600">Right-click</strong> the link below and select "Open in new tab" (or Ctrl+Click / Cmd+Click).
                Keep THIS page open — you'll need to return here!
              </p>
              <a
                href={COUNCIL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-indigo-600 underline hover:text-indigo-800 text-xs mt-1"
              >
                Open council form in new tab <ExternalLink size={11} className="inline" />
              </a>
            </div>
          </div>

          {/* Step 2b */}
          <div className="flex gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs shrink-0 mt-0.5">2</span>
            <div>
              <p className="font-medium">In the council tab, click "AHTemple Batch 1/{totalBatches}" in your bookmarks bar</p>
              <p className="text-gray-500 text-xs mt-0.5">
                The form will fill in automatically. Wait until you see
                "<strong>All Sites Filled</strong>" in the green bar at the top.
              </p>
            </div>
          </div>

          {/* Step 2c */}
          <div className="flex gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs shrink-0 mt-0.5">3</span>
            <div>
              <p className="font-medium">Submit the last page on the council site</p>
              <p className="text-gray-500 text-xs mt-0.5">
                After the last site, click the Submit button on the council
                page to submit your final response.
              </p>
            </div>
          </div>

          {/* Step 2d */}
          <div className="flex gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs shrink-0 mt-0.5">4</span>
            <div>
              <p className="font-medium">Repeat for the next batch (in the council tab)</p>
              <p className="text-gray-500 text-xs mt-0.5">
                Open a fresh council form page in the same tab (or a new one), then click
                the next bookmark (Batch 2/{totalBatches}, then 3/{totalBatches}, and so on).
                Each batch needs a fresh council page.
              </p>
            </div>
          </div>

          {/* Step 2e - Confirm */}
          <div className="flex gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-700 font-bold text-xs shrink-0 mt-0.5">5</span>
            <div>
              <p className="font-medium text-green-700">Return to THIS page and confirm</p>
              <p className="text-gray-500 text-xs mt-0.5">
                After completing all batches, <strong>come back to this tab</strong> and scroll down to click the green
                "I've Completed All My Submissions" button below.
              </p>
            </div>
          </div>
        </div>

        {/* Time estimate */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex gap-2 text-sm text-amber-800">
          <Info size={15} className="shrink-0 mt-0.5" />
          <span>
            Each batch takes about 1 minute. Total time for all{" "}
            {totalBatches} batches: ~{totalBatches} minutes.
            Keep this page open in a separate tab!
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Site selection panel (collapsed by default)
// ─────────────────────────────────────────────────────────────
function SiteSelector({ sites, selectedRefs, onToggle, onSelectAll, onDeselectAll }) {
  const [expanded, setExpanded] = useState(false);
  const count = selectedRefs.size;
  const total = sites.length;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* Header row */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm"
      >
        <span className="flex items-center gap-2 font-medium text-gray-700">
          <CheckSquare size={15} className="text-indigo-600" />
          {count === total ? (
            <span>
              All <strong>{total}</strong> sites selected{" "}
              <span className="text-green-600 font-semibold">✓</span>
            </span>
          ) : (
            <span>
              <strong>{count}</strong> of {total} sites selected
            </span>
          )}
        </span>
        <span className="flex items-center gap-1 text-xs text-indigo-600 font-medium">
          {expanded ? "Collapse" : "Review / change"}
          {expanded ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronRight size={14} />
          )}
        </span>
      </button>

      {/* Site list */}
      {expanded && (
        <div className="border-t border-gray-200">
          {/* Bulk controls */}
          <div className="flex gap-3 px-4 py-2 bg-white border-b border-gray-100">
            <button
              type="button"
              onClick={onSelectAll}
              className="text-xs text-indigo-600 hover:underline font-medium"
            >
              Select all
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={onDeselectAll}
              className="text-xs text-gray-500 hover:text-red-500 hover:underline"
            >
              Deselect all
            </button>
          </div>

          {/* Site rows — compact */}
          <div className="max-h-64 overflow-y-auto divide-y divide-gray-50">
            {sites.map((site) => {
              const ref = site.site_reference || site.reference_number;
              const checked = selectedRefs.has(ref);
              return (
                <label
                  key={ref}
                  className="flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
                    checked={checked}
                    onChange={() => onToggle(ref)}
                  />
                  <span className="text-xs text-gray-700 leading-snug">
                    <span className="font-mono text-gray-400 mr-1.5">{ref}</span>
                    {site.site_address || site.address}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Social sharing row
// ─────────────────────────────────────────────────────────────
function ShareRow() {
  const message = encodeURIComponent(SHARE_MESSAGE + APP_URL);
  const url = encodeURIComponent(APP_URL);

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span className="text-sm text-gray-500 font-medium flex items-center gap-1.5">
        <Share2 size={14} /> Share:
      </span>
      <a
        href={`https://wa.me/?text=${message}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
      >
        💬 WhatsApp
      </a>
<a
        href={`https://twitter.com/intent/tweet?text=${message}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 bg-black hover:bg-gray-800 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
      >
        𝕏 Post
      </a>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Input component
// ─────────────────────────────────────────────────────────────
function Field({ label, required, error, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
        {!required && (
          <span className="text-gray-400 font-normal text-xs ml-1">
            (optional)
          </span>
        )}
      </label>
      {children}
      {hint && !error && (
        <p className="text-xs text-gray-400 mt-1">{hint}</p>
      )}
      {error && (
        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
          <AlertCircle size={11} /> {error}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
export default function PublicGenerator() {
  const [mobile] = useState(isMobile);

  // Form fields
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [streetName, setStreetName] = useState("");
  const [yearsInAylesbury, setYearsInAylesbury] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Site data + selection
  const [allSites, setAllSites] = useState([]);
  const [selectedRefs, setSelectedRefs] = useState(new Set());
  const [sitesLoaded, setSitesLoaded] = useState(false);

  // Bookmarklet state
  const [bookmarklet, setBookmarklet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seed] = useState(() => Math.floor(Math.random() * 99999));
  
  // Confirmation state
  const [confirmationStatus, setConfirmationStatus] = useState(null); // null | 'confirming' | 'confirmed' | 'error'
  const [bookmarkletCreatedAt, setBookmarkletCreatedAt] = useState(null);

  const outputRef = useRef(null);

  // Load sites list on mount (just references and addresses, no full prefill yet)
  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/sites`)
      .then((res) => {
        const sites = res.data.sites || [];
        setAllSites(sites);
        setSelectedRefs(new Set(sites.map((s) => s.reference_number)));
        setSitesLoaded(true);
      })
      .catch(() => setSitesLoaded(true)); // fail silently; sites shown from prefill later
  }, []);

  // Site toggle handlers
  const toggleSite = (ref) =>
    setSelectedRefs((prev) => {
      const next = new Set(prev);
      next.has(ref) ? next.delete(ref) : next.add(ref);
      return next;
    });
  const selectAll = () =>
    setSelectedRefs(new Set(allSites.map((s) => s.reference_number)));
  const deselectAll = () => setSelectedRefs(new Set());

  // Handle confirmation of submission
  const handleConfirmSubmission = async () => {
    const token = localStorage.getItem("cf_session_token");
    if (!token) {
      setError("No session found. Please create a new bookmarklet.");
      return;
    }
    
    setConfirmationStatus('confirming');
    try {
      await axios.post(`${BACKEND_URL}/api/public/confirm-complete/${token}`, {
        final_submitted: true
      });
      setConfirmationStatus('confirmed');
    } catch (err) {
      console.error("Confirmation error:", err);
      setConfirmationStatus('error');
    }
  };

  const handleCreateBookmarklet = async () => {
    // ── Validation ───────────────────────────────────────────
    const errors = {};
    if (!userEmail.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail.trim())) {
      errors.email = "Please enter a valid email address";
    }
    const streetErr = validateStreetName(streetName);
    const yearsErr = validateYears(yearsInAylesbury);
    if (streetErr) errors.streetName = streetErr;
    if (yearsErr) errors.years = yearsErr;
    if (!consentGiven) errors.consent = "Please agree to the terms to continue";
    if (selectedRefs.size === 0)
      errors.sites = "Please select at least one site";
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    setError(null);
    setBookmarklet(null);

    try {
      // 1. Generate prefill data (with personal context woven in server-side)
      let prefillUrl = `${BACKEND_URL}/api/generate/prefill?seed=${seed}`;
      if (streetName.trim())
        prefillUrl += `&street_name=${encodeURIComponent(streetName.trim())}`;
      if (yearsInAylesbury.trim())
        prefillUrl += `&years_in_aylesbury=${encodeURIComponent(yearsInAylesbury.trim())}`;
      const prefillRes = await axios.get(prefillUrl);
      const allPrefill = prefillRes.data.sites || [];

      // 2. Filter to only selected sites
      const sitesToSubmit = allPrefill.filter((s) =>
        selectedRefs.has(s.site_reference)
      );

      // 3. Create session (stores GDPR data + selected_site_refs in DB)
      const sessionRes = await publicApi.session(
        sitesToSubmit,
        consentGiven,
        CONSENT_TEXT,
        userName.trim(),
        userEmail.trim(),
        "" // phone not collected in main flow
      );
      const token = sessionRes.data.token;

      // 4. Generate loader bookmarklet
      const loaderRes = await publicApi.loaderBookmarklet(
        token,
        BACKEND_URL,
        seed
      );
      setBookmarklet(loaderRes.data);
      localStorage.setItem("cf_session_token", token);
      setBookmarkletCreatedAt(Date.now());
      setConfirmationStatus(null);

      // Scroll to output
      setTimeout(
        () => outputRef.current?.scrollIntoView({ behavior: "smooth" }),
        150
      );
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      // Friendly duplicate-email message
      if (err?.response?.status === 409) {
        setError(
          "This email has already been used. One response per person. If you need help, visit aylesburyhindutemple.org."
        );
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Mobile gate ─────────────────────────────────────────────
  if (mobile) return <MobileBlocker />;

  const selectedCount = selectedRefs.size;
  const totalSites = allSites.length || 32;

  // Build bookmarklet display values
  const bmBatches = bookmarklet?.batches || [];
  const bmBatchSize = bookmarklet?.batch_size || 5;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-amber-50">
      {/* ── Header ─────────────────────────────────────────── */}
      <header className="bg-white/90 backdrop-blur border-b border-slate-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <img
              src="/aht_full_brand.png"
              alt="Aylesbury Hindu Temple"
              className="h-12 w-auto object-contain shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-slate-900 truncate">
                Aylesbury Hindu Temple
              </h1>
              <p className="text-xs text-slate-600 mt-0.5">
                Council Consultation — Local Plan Sites Survey
              </p>
            </div>
          </div>
          <a
            href="https://www.aylesburyhindutemple.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium text-orange-600 hover:text-orange-700 hover:underline shrink-0"
          >
            aylesburyhindutemple.org ↗
          </a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10 space-y-8">
        {/* ── Hero ───────────────────────────────────────────── */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-full">
            🛕 Aylesbury Hindu Temple CIO · Charity no. 1209109
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 leading-tight">
            Support a Hindu temple{" "}
            <span className="text-indigo-600">within or nearby Aylesbury</span>
          </h2>
          <p className="text-gray-600 max-w-lg mx-auto leading-relaxed">
            Fill in your details below and we'll create a personalised bookmark
            that automatically submits your response to the council's Local Plan
            Sites Survey — covering all {totalSites} potential sites in about{" "}
            <strong>15 minutes</strong>.
          </p>
        </div>

        {/* ── Form card ─────────────────────────────────────── */}
        {!bookmarklet && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-7 space-y-6">
            {/* Email */}
            <Field
              label="Your email address"
              required
              error={fieldErrors.email}
            >
              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder="you@example.com"
                className={`w-full rounded-xl border px-4 py-2.5 text-sm text-slate-900 placeholder-slate-500 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                  fieldErrors.email
                    ? "border-red-400 bg-red-50"
                    : "border-slate-300"
                }`}
              />
            </Field>

            {/* Name */}
            <Field label="Your name" error={fieldErrors.name}>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Your full name"
                className="w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </Field>

            {/* Street + Years — side by side */}
            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Your street or area"
                error={fieldErrors.streetName}
                hint="Helps personalise your response"
              >
                <input
                  type="text"
                  value={streetName}
                  onChange={(e) => setStreetName(e.target.value)}
                  placeholder="e.g. Walton Street"
                  className={`w-full rounded-xl border px-4 py-2.5 text-sm text-slate-900 placeholder-slate-500 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                    fieldErrors.streetName
                      ? "border-red-400 bg-red-50"
                      : "border-slate-300"
                  }`}
                />
              </Field>
              <Field
                label="Years in Aylesbury"
                error={fieldErrors.years}
                hint="Adds credibility to your response"
              >
                <input
                  type="text"
                  inputMode="numeric"
                  value={yearsInAylesbury}
                  onChange={(e) => setYearsInAylesbury(e.target.value)}
                  placeholder="e.g. 12"
                  className={`w-full rounded-xl border px-4 py-2.5 text-sm text-slate-900 placeholder-slate-500 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                    fieldErrors.years
                      ? "border-red-400 bg-red-50"
                      : "border-slate-300"
                  }`}
                />
              </Field>
            </div>

            {/* Site selection */}
            {sitesLoaded && allSites.length > 0 && (
              <div>
                <SiteSelector
                  sites={allSites}
                  selectedRefs={selectedRefs}
                  onToggle={toggleSite}
                  onSelectAll={selectAll}
                  onDeselectAll={deselectAll}
                />
                {fieldErrors.sites && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={11} /> {fieldErrors.sites}
                  </p>
                )}
              </div>
            )}

            {/* GDPR Consent */}
            <label
              className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                consentGiven
                  ? "border-green-300 bg-green-50"
                  : fieldErrors.consent
                  ? "border-red-300 bg-red-50"
                  : "border-gray-200 bg-gray-50 hover:bg-gray-100"
              }`}
            >
              <input
                type="checkbox"
                className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
                checked={consentGiven}
                onChange={(e) => setConsentGiven(e.target.checked)}
              />
              <span className="text-xs text-gray-600 leading-relaxed">
                <Shield
                  size={12}
                  className="inline mr-1 text-indigo-500 align-middle"
                />
                {CONSENT_TEXT}
              </span>
            </label>
            {fieldErrors.consent && (
              <p className="text-xs text-red-500 -mt-4 flex items-center gap-1">
                <AlertCircle size={11} /> {fieldErrors.consent}
              </p>
            )}

            {/* Error banner */}
            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                <AlertCircle size={15} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit button */}
            <button
              onClick={handleCreateBookmarklet}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-bold py-4 rounded-xl text-base transition-colors shadow-md hover:shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Creating your bookmark…
                </>
              ) : (
                <>
                  <Bookmark size={18} />
                  Create My Bookmark — {selectedCount} sites
                </>
              )}
            </button>

            <p className="text-center text-xs text-slate-500">
              Takes about 1 minute per batch · AHT records your IP for GDPR purposes ·{" "}
              <a
                href="https://www.aylesburyhindutemple.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-600 hover:underline"
              >
                aylesburyhindutemple.org
              </a>
            </p>
          </div>
        )}

        {/* ── Bookmarklet output + drag guide ───────────────── */}
        {bookmarklet && (
          <div ref={outputRef} className="space-y-6">
            <BatchWorkflow
              batches={bmBatches}
              totalSites={selectedCount}
              batchSize={bmBatchSize}
            />

            {/* Change my mind — start over */}
            <div className="text-center">
              <button
                onClick={() => {
                  setBookmarklet(null);
                  setError(null);
                  localStorage.removeItem("cf_session_token");
                  setConfirmationStatus(null);
                  setBookmarkletCreatedAt(null);
                }}
                className="text-xs text-gray-400 hover:text-gray-600 underline"
              >
                Start over / use a different email
              </button>
            </div>

            {/* ── Confirmation Section ───────────────────────────── */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 rounded-full p-2">
                  <CheckCircle2 size={24} className="text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-green-800 text-lg">Finished your submissions?</h3>
                  <p className="text-sm text-green-700 mt-1">
                    After you've clicked "Submit" on the council form for your final site, 
                    come back here and click the button below to confirm your submissions are complete.
                  </p>
                </div>
              </div>
              
              {confirmationStatus === 'confirmed' ? (
                <div className="bg-green-100 border border-green-300 rounded-xl p-4 flex items-center gap-3">
                  <CheckCircle2 size={28} className="text-green-600" />
                  <div>
                    <p className="font-bold text-green-800">🎉 Thank you! Your submission has been confirmed.</p>
                    <p className="text-sm text-green-700">We've recorded your completed consultation response.</p>
                  </div>
                </div>
              ) : confirmationStatus === 'error' ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-red-700 text-sm">There was an error confirming your submission. Please try again.</p>
                  <button
                    onClick={handleConfirmSubmission}
                    className="mt-2 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg text-sm"
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleConfirmSubmission}
                  disabled={confirmationStatus === 'confirming'}
                  className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-bold py-4 rounded-xl text-lg transition-colors shadow-md hover:shadow-lg"
                >
                  {confirmationStatus === 'confirming' ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Confirming...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={20} />
                      I've Completed All My Submissions ✓
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Social sharing ─────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3">
          <p className="text-sm font-semibold text-gray-700">
            Spread the word — every response counts
          </p>
          <ShareRow />
        </div>

        {/* ── About AHT ──────────────────────────────────────── */}
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 text-sm text-amber-900 space-y-1">
          <p className="font-semibold">About Aylesbury Hindu Temple CIO</p>
          <p className="text-xs leading-relaxed text-amber-800">
            Aylesbury Hindu Temple Charitable Incorporated Organisation (charity
            no. 1209109) is seeking to secure a permanent site within or nearby
            Aylesbury for a Hindu Mandir, community hall, and cultural centre
            serving nearly 2,000 Hindu residents in the town. The temple will
            provide a place of worship, cultural education, food bank support,
            and community cohesion events open to all.
          </p>
          <a
            href="https://www.aylesburyhindutemple.org"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline font-medium mt-1"
          >
            <ExternalLink size={11} /> www.aylesburyhindutemple.org
          </a>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 pb-6">
          © Aylesbury Hindu Temple CIO · Charity no. 1209109 ·{" "}
          <a
            href="/volunteer"
            className="hover:underline text-indigo-400"
          >
            Can't use a laptop? Let a volunteer help
          </a>
          <br />
          <span className="text-gray-500 mt-1 inline-block">
            Built by{" "}
            <a
              href="https://scaleright.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline text-indigo-400"
            >
              ScaleRight.ai
            </a>
          </span>
        </p>
      </main>
    </div>
  );
}
